import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Expense, ExpenseNameMap, ExpenseOrderMap } from '../_models/expense';
import { Package, Rule } from '../_models/package';
import { Question } from '../_models/question';
import { CalcRules, SchoolProperties } from '../_models/calcPackage';
import { Preferences } from '../_models/preferences';
import { School } from './schools.service';
import { formatAidYear } from '../_shared/aid-year/aid-year.config';

export interface SchoolContent {
  welcomeIntro: string;
  welcomeDisclaimer: string;
  nextSteps: string;
  schoolQuestionHeader: string;
}

const RESERVED_SCHOOL_CODES = new Set([
  'schools',
  'default',
  'no-access',
  'epl-login',
  'support',
  'admin-login',
  'defaults',
]);

export interface CreateSchoolInput {
  code: string;
  name: string;
  institutionalMethod: boolean;
  orgId: string;
  aidYear: number;
  content: SchoolContent;
  createdAt: Date;
  allQuestions: Question[];
}

export interface CreateSchoolDocuments {
  school: School;
  preferences: Partial<Preferences>;
  calcRules: CalcRules;
  questions: Question[];
}

export function normalizeSchoolCode(code: string): string {
  return code.trim().toLowerCase();
}

export function schoolCodeError(code: string): string | null {
  const normalized = normalizeSchoolCode(code);
  if (!normalized) {
    return 'Enter a school code.';
  }
  if (!/^[a-z0-9-]+$/.test(normalized)) {
    return 'Use lowercase letters, numbers, and hyphens only.';
  }
  if (normalized.startsWith('-') || normalized.endsWith('-')) {
    return 'The school code cannot start or end with a hyphen.';
  }
  if (RESERVED_SCHOOL_CODES.has(normalized)) {
    return 'That school code is reserved.';
  }
  return null;
}

export function schoolCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const message = schoolCodeError(String(control.value ?? ''));
    return message ? { schoolCode: message } : null;
  };
}

export function starterExpenses(): Expense[] {
  return (Object.keys(ExpenseOrderMap) as Array<keyof typeof ExpenseOrderMap>).map(type => ({
    type,
    name: ExpenseNameMap[type],
    order: ExpenseOrderMap[type],
    rules: [{ populationId: '1', amount: 0 }],
  }));
}

export function starterPackages(institutionalMethod: boolean): Package[] {
  const fmKey = 'inarMiscCalculations.efmSAINoNegative';
  return [
    requiredPackage(
      'AdjustedParentContribution',
      1,
      institutionalMethod ? 'inarParentIMResults.ContributionForStudent' : fmKey,
    ),
    requiredPackage(
      'AdjustedStudentContribution',
      2,
      institutionalMethod ? 'inarStudentIMResults.ContributionForStudent' : fmKey,
    ),
    requiredPackage('PellGrant', 3, 'rt.PellGrant'),
  ];
}

export function starterQuestions(allQuestions: Question[], institutionalMethod: boolean): Question[] {
  return allQuestions
    .filter(question => institutionalMethod ? question.imRequired === true : question.fmRequired === true)
    .map(question => ({ ...question, methodRequired: true }));
}

export function buildCreateSchool(input: CreateSchoolInput): CreateSchoolDocuments {
  const code = normalizeSchoolCode(input.code);
  const schoolProperties = schoolPropertiesFor(input);
  return {
    school: {
      name: input.name.trim(),
      demo: false,
      status: 'COMING',
      disabledDate: input.createdAt,
      schoolProperties,
    },
    preferences: preferencesFor(code, input, schoolProperties),
    calcRules: calcRulesFor(input, schoolProperties),
    questions: starterQuestions(input.allQuestions, input.institutionalMethod),
  };
}

function schoolPropertiesFor(input: CreateSchoolInput): SchoolProperties {
  return {
    draftAidYear: input.aidYear,
    aidYear: input.aidYear,
    orgId: input.orgId.trim(),
    cssId: '',
    institutionalMethod: input.institutionalMethod,
  };
}

function preferencesFor(code: string, input: CreateSchoolInput, schoolProperties: SchoolProperties): Partial<Preferences> {
  const name = input.name.trim();
  return {
    univ_id: code,
    status: 'COMING',
    disableSchool: true,
    universityName: name,
    shortName: name.slice(0, 20),
    npcExitUrl: '',
    aidYear: formatAidYear(schoolProperties.aidYear),
    showEFC: true,
    content: { ...input.content },
    headerType: 'hSolid',
    headerColor: '#F4F5F4',
    backgroundType: 'bgSolid',
    panelColor: '#FFFFFF',
    footerColor: '#f5f5f5',
    useRounding: true,
    efcRounding: false,
  };
}

function calcRulesFor(input: CreateSchoolInput, schoolProperties: SchoolProperties): CalcRules {
  return {
    expenses: starterExpenses(),
    packages: starterPackages(input.institutionalMethod),
    populations: [],
    tables: [],
    formulas: [],
    useRounding: true,
    roundingValue: 50,
    efcRounding: false,
    schoolProperties,
  };
}

function requiredPackage(id: string, order: number, keyId: string): Package {
  const rule: Rule = {
    assignType: 'Factor',
    category: 'Assignment',
    enabled: true,
    factor: 100,
    keyId,
    need_based: true,
    order: 1,
    populationId: '1',
    populationOperator: 'EQ',
    type: 'Calculation',
  };
  return {
    enabled: true,
    id,
    order,
    rules: [rule],
  };
}
